import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import Pagination from "../../components/ui/Pagination";
import BranchSelect from "../../components/ui/BranchSelect";
import { attendanceApi } from "../../api/attendanceApi";
import {
  useBiometricImport,
  useBiometricImports,
  useBiometricLayouts,
  useBiometricPresets,
  useImportBiometrics,
} from "../../api/queries/attendanceQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime } from "../../utils/dateTime";
import { downloadTextFile } from "../../utils/csv";
import DryRunResult, { Stat } from "./components/biometrics/DryRunResult";
import LayoutsTab from "./components/biometrics/LayoutsTab";
import LinksTab from "./components/biometrics/LinksTab";
import {
  candidateLabel,
  importParamsOf,
  sourceOfLayout,
} from "./components/biometrics/bioConfig";
import "./attendance.css";
import { useCanModify } from "../../hooks/usePermissions";
import usePermissions from "../../hooks/usePermissions";

const TABS = [
  { key: "upload", label: "Upload", icon: "bi-upload" },
  { key: "layouts", label: "Layouts", icon: "bi-layout-text-window" },
  { key: "ids", label: "Employee IDs", icon: "bi-fingerprint" },
];

const EMPTY = [];
const ACCEPT = ".csv,.txt,.dat,.xlsx,.xls";
const MAX_BYTES = 10 * 1024 * 1024;

/** Row errors of a past import (strings or { row, employeeNumber, date, message }). */
const ErrorTable = ({ errors }) =>
  errors.length === 0 ? null : (
    <div className="table-responsive bio-errors">
      <table className="table table-sm mb-0">
        <thead>
          <tr>
            <th>Row</th>
            <th>Employee ID</th>
            <th>Date</th>
            <th>Problem</th>
          </tr>
        </thead>
        <tbody>
          {errors.slice(0, 200).map((e, i) =>
            typeof e === "string" ? (
              <tr key={i}>
                <td colSpan={3}>—</td>
                <td>{e}</td>
              </tr>
            ) : (
              <tr key={i}>
                <td className="tabular-nums">{e.row ?? "—"}</td>
                <td>{e.employeeNumber || "—"}</td>
                <td className="text-nowrap">{e.date || "—"}</td>
                <td>{e.message || "—"}</td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      {errors.length > 200 && (
        <div className="fs-075 text-slate-500 p-2">
          Showing the first 200 of {errors.length}.
        </div>
      )}
    </div>
  );

/** One past import: counts and row errors. */
const ImportDetails = ({ id, onClose }) => {
  const query = useBiometricImport(id);
  const r = query.data;
  return (
    <Dialog
      title="Biometrics import"
      subtitle={
        r ? `${r.fileName} · ${formatDateTime(r.createdAt)}` : undefined
      }
      size="lg"
      onClose={onClose}
    >
      {query.isLoading && (
        <div className="text-center py-4">
          <span
            className="spinner-border text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      )}
      {query.isError && (
        <div className="alert alert-danger fs-085">
          {getApiErrorMessage(query.error, "Could not load this import.")}
        </div>
      )}
      {r && (
        <>
          <div className="bio-stats">
            <Stat label="Rows" value={r.totalRows} />
            <Stat label="Imported" value={r.imported} tone="ok" />
            <Stat label="Duplicates" value={r.duplicates} />
            <Stat
              label="Failed"
              value={r.failed}
              tone={r.failed ? "bad" : undefined}
            />
            <Stat label="Days merged" value={r.daysMerged} />
            <Stat label="Days skipped" value={r.daysSkipped} />
          </div>
          <ErrorTable errors={r.errors || EMPTY} />
        </>
      )}
    </Dialog>
  );
};

/** Upload: layout → file → dry run (+ link suggested IDs) → import; history below. */
const UploadTab = ({ onOpenLayouts }) => {
  const canModify = useCanModify("attendance", { company: true });
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [source, setSource] = useState(""); // "" = automatic, "t:<id>" = saved layout, "p:<key>" = preset
  const [branchId, setBranchId] = useState("");
  const [dryResult, setDryResult] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [linkSuggested, setLinkSuggested] = useState(false);
  const [page, setPage] = useState(1);
  const [openImport, setOpenImport] = useState(null);

  const layouts = useBiometricLayouts().data || EMPTY;
  const presets = useBiometricPresets().data || EMPTY;
  const importer = useImportBiometrics();
  const imports = useBiometricImports({ page, limit: 10 });
  const history = imports.data?.data || EMPTY;

  const reset = () => {
    setDryResult(null);
    setResult(null);
    setError(null);
    setLinkSuggested(false);
  };
  const pick = (f) => {
    reset();
    if (f && f.size > MAX_BYTES) {
      setError("The file is larger than 10 MB.");
      setFile(null);
      return;
    }
    setFile(f || null);
  };

  const paramsOf = (value) => ({
    templateId: value.startsWith("t:") ? value.slice(2) : undefined,
    preset: value.startsWith("p:") ? value.slice(2) : undefined,
  });
  // The real import sends the layout the dry run showed, so it can't shift
  const importParams = dryResult ? importParamsOf(dryResult, presets) : null;

  const run = (dryRun, value = source) =>
    importer.mutate(
      {
        file,
        dryRun,
        ...(dryRun ? paramsOf(value) : importParams),
        branchId: branchId || undefined,
        linkSuggested: !dryRun && linkSuggested,
      },
      {
        onSuccess: (res) => {
          setError(null);
          if (dryRun) setDryResult(res);
          else {
            setResult(res);
            setDryResult(null);
            setFile(null);
            setLinkSuggested(false);
            if (fileRef.current) fileRef.current.value = "";
            toast.success(
              `Imported ${res.imported ?? 0} punches; ${res.daysMerged ?? 0} day(s) merged.`,
            );
          }
        },
        onError: (err) => {
          // A failed dry run can't back an import
          if (dryRun) setDryResult(null);
          const msg = err?.response?.data?.message;
          setError(
            Array.isArray(msg)
              ? msg.join(" ")
              : getApiErrorMessage(err, "Could not read the file."),
          );
        },
      },
    );

  // Another layout for the same file: dry run again with it
  const changeLayout = (value) => {
    setSource(value);
    setLinkSuggested(false);
    setResult(null);
    run(true, value);
  };
  const shownSource = dryResult
    ? sourceOfLayout(dryResult.template, presets)
    : "";
  const candidates = dryResult?.candidates || EMPTY;
  const candidateValues = candidates.map((c) => sourceOfLayout(c, presets));

  const downloadTemplate = async () => {
    try {
      const blob = await attendanceApi.getBiometricTemplate();
      const text = typeof blob === "string" ? blob : await blob.text();
      downloadTextFile(text, "biometrics_template.csv");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not download the template."));
    }
  };

  return (
    <>
      <div className="att-card mb-4">
        <ol className="bio-steps">
          <li>
            <strong>Choose the layout</strong> of your device export (.csv,
            .txt, .dat, .xlsx or .xls, up to 10 MB), or leave it on Automatic to
            use the branch's layout or the one that reads the file best. No
            layout for your device yet?{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={onOpenLayouts}
            >
              Create one from a sample
            </button>
            , or use the{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={downloadTemplate}
            >
              CSV template
            </button>
            . Times are Manila time.
          </li>
          <li>
            <strong>Dry run</strong> to check the file without changing
            anything.
          </li>
          <li>
            <strong>Import</strong>. Days whose DTR is already submitted or
            approved are skipped.
          </li>
        </ol>

        <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
          <select
            className="form-select w-auto"
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              reset();
            }}
            aria-label="Layout"
          >
            <option value="">Automatic</option>
            {layouts.length > 0 && (
              <optgroup label="Saved layouts">
                {layouts.map((l) => (
                  <option key={l.id} value={`t:${l.id}`}>
                    {l.name}
                    {l.system ? ` (${l.system})` : ""}
                    {l.isDefault ? " · default" : ""}
                  </option>
                ))}
              </optgroup>
            )}
            {presets.length > 0 && (
              <optgroup label="Presets">
                {presets.map((p) => (
                  <option key={p.key} value={`p:${p.key}`}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <div className="att-filter-branch">
            <BranchSelect
              value={branchId}
              onChange={(v) => {
                setBranchId(v || "");
                reset();
              }}
              emptyLabel="Branch (optional)"
              id="bio-branch"
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            className="form-control bio-file"
            accept={ACCEPT}
            onChange={(e) => pick(e.target.files?.[0])}
            aria-label="Biometrics file"
          />
          <button
            type="button"
            className="btn btn-outline-primary profile-btn bg-white"
            onClick={() => run(true)}
            disabled={!file || importer.isPending}
          >
            {importer.isPending && importer.variables?.dryRun && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            <i className="bi bi-search"></i> Dry run
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={() => run(false)}
            disabled={
              !canModify ||
              !file ||
              importer.isPending ||
              !dryResult ||
              !importParams
            }
            title={
              !canModify
                ? "View-only access"
                : !dryResult
                  ? "Run a dry run first"
                  : !importParams
                    ? "Choose a layout and run the dry run again"
                    : undefined
            }
          >
            {importer.isPending && !importer.variables?.dryRun && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            <i className="bi bi-upload"></i> Import
          </button>
        </div>

        {error && (
          <div className="alert alert-danger fs-085 mt-3 mb-0">{error}</div>
        )}

        {dryResult && (
          <div className="mt-3">
            <h2 className="bio-subtitle">
              Dry run{dryResult.fileName ? `: ${dryResult.fileName}` : ""}
            </h2>
            <DryRunResult
              result={dryResult}
              linkSuggested={linkSuggested}
              onLinkSuggested={setLinkSuggested}
            />
            {dryResult.template?.chosenBy === "DETECTED" && (
              <p className="fs-080 text-slate-600 mb-2">
                Detection only compares how many rows can be read, not whether
                the employees match. Check the employee results before you
                import.
              </p>
            )}
            {!importParams && (
              <div className="alert alert-warning fs-080 py-2" role="alert">
                This layout can't be used for the import. Choose one below.
              </div>
            )}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <label htmlFor="bio-change-layout" className="fs-080 m-0">
                Use a different layout:
              </label>
              <select
                id="bio-change-layout"
                className="form-select form-select-sm w-auto"
                value={shownSource}
                disabled={importer.isPending}
                onChange={(e) => changeLayout(e.target.value)}
              >
                {!shownSource && <option value="">Choose a layout</option>}
                {candidates.length > 0 && (
                  <optgroup label="Tried on this file, best first">
                    {candidates.map((c, i) =>
                      candidateValues[i] ? (
                        <option
                          key={candidateValues[i]}
                          value={candidateValues[i]}
                        >
                          {candidateLabel(c)}
                        </option>
                      ) : null,
                    )}
                  </optgroup>
                )}
                <optgroup label="Saved layouts">
                  {layouts
                    .filter((l) => !candidateValues.includes(`t:${l.id}`))
                    .map((l) => (
                      <option key={l.id} value={`t:${l.id}`}>
                        {l.name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Presets">
                  {presets
                    .filter((p) => !candidateValues.includes(`p:${p.key}`))
                    .map((p) => (
                      <option key={p.key} value={`p:${p.key}`}>
                        {p.name}
                      </option>
                    ))}
                </optgroup>
              </select>
              {importer.isPending && importer.variables?.dryRun && (
                <span
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                  aria-label="Checking the file"
                ></span>
              )}
            </div>
            {dryResult.failed > 0 && (
              <p className="fs-080 text-slate-600 mt-2 mb-0">
                Rows that failed are left out of the import. Fix them in the
                file and run again, or import the rest.
              </p>
            )}
          </div>
        )}

        {result && (
          <div className="mt-3">
            <h2 className="bio-subtitle">
              Imported{result.fileName ? `: ${result.fileName}` : ""}
            </h2>
            <DryRunResult result={result} imported />
          </div>
        )}
      </div>

      <h2 className="bio-subtitle">Import history</h2>
      <div className="glass-table-card">
        {imports.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(imports.error, "Could not load the imports.")}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0 att-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">File</th>
                    <th className="py-3">Uploaded</th>
                    <th className="py-3 text-end">Rows</th>
                    <th className="py-3 text-end">Imported</th>
                    <th className="py-3 text-end">Duplicates</th>
                    <th className="py-3 text-end">Failed</th>
                    <th className="py-3 text-end pe-4">
                      Days merged / skipped
                    </th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {history.map((r) => (
                    <tr
                      key={r.id}
                      className="cursor-pointer"
                      onClick={() => setOpenImport(r.id)}
                    >
                      <td className="ps-4 fw-semibold">{r.fileName}</td>
                      <td className="text-nowrap">
                        {formatDateTime(r.createdAt)}
                      </td>
                      <td className="text-end tabular-nums">
                        {r.totalRows ?? 0}
                      </td>
                      <td className="text-end tabular-nums">
                        {r.imported ?? 0}
                      </td>
                      <td className="text-end tabular-nums">
                        {r.duplicates ?? 0}
                      </td>
                      <td
                        className={`text-end tabular-nums ${r.failed ? "text-danger fw-bold" : ""}`}
                      >
                        {r.failed ?? 0}
                      </td>
                      <td className="text-end tabular-nums pe-4">
                        {r.daysMerged ?? 0} / {r.daysSkipped ?? 0}
                      </td>
                    </tr>
                  ))}
                  {!imports.isLoading && history.length === 0 && (
                    <tr>
                      <td colSpan={7} className="approval-empty">
                        <i className="bi bi-fingerprint"></i>No imports yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {imports.isLoading && (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
            <Pagination
              page={page}
              limit={10}
              total={imports.data?.total || 0}
              onPageChange={setPage}
              onLimitChange={() => {}}
              pageSizes={[10]}
            />
          </>
        )}
      </div>

      {openImport && (
        <ImportDetails id={openImport} onClose={() => setOpenImport(null)} />
      )}
    </>
  );
};

/**
 * Admin/HR: upload biometric device exports and merge them with the app
 * check-ins. Layouts say how each device's file is read; Employee IDs link
 * device IDs to employees. Days whose DTR is submitted/approved are skipped.
 */
const BiometricsPage = () => {
  // HR-level (attendance with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("attendance");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "upload";

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="att-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can upload
            biometrics.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-3">
          <h1 className="fw-bold text-dark m-0 page-title">Biometrics</h1>
          <p className="text-muted small mb-0">
            Merge biometric device punches with the employees' app check-ins.
          </p>
        </div>
        <div className="bio-tabs mb-4" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`bio-tab ${tab === t.key ? "active" : ""}`}
              onClick={() =>
                setSearchParams(t.key === "upload" ? {} : { tab: t.key }, {
                  replace: true,
                })
              }
            >
              <i className={`bi ${t.icon}`}></i> {t.label}
            </button>
          ))}
        </div>
        {tab === "upload" && (
          <UploadTab
            onOpenLayouts={() => setSearchParams({ tab: "layouts" })}
          />
        )}
        {tab === "layouts" && <LayoutsTab />}
        {tab === "ids" && <LinksTab />}
      </div>
    </Layout>
  );
};

export default BiometricsPage;
